import { BACKEND_URL } from '@/config';
﻿import React, { useState, useRef, useEffect } from 'react';
import { X, AlertTriangle, Upload, ImageIcon, Trash2, Clock } from 'lucide-react';
import axios from 'axios';
import { useToast } from '@/context/ToastContext';
import { compressImage } from '@/utils/photos';

const DISPUTE_WINDOW_MS = 48 * 60 * 60 * 1000;

function fmtCountdown(ms) {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m restantes`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s restantes`;
  return `${s}s restantes`;
}

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const DISPUTE_CATEGORIES = [
  { v: 'no_show',    l: 'Transporteur absent / non présenté' },
  { v: 'damage',     l: 'Colis endommagé' },
  { v: 'lost',       l: 'Colis perdu / non livré' },
  { v: 'wrong_item', l: 'Mauvais colis livré' },
  { v: 'late',       l: 'Retard important' },
  { v: 'no_payment', l: 'Problème de paiement' },
  { v: 'fraud',      l: 'Fraude suspectée' },
  { v: 'other',      l: 'Autre' },
];

export default function DisputeModal({ open, onClose, missionId, mission, onSuccess }) {
  const toast = useToast();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null); // null=unrestricted, number=ms left, 'expired'
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (mission?.status !== 'COMPLETED') { setTimeLeft(null); return; }
    const confirmedAt = mission?.confirmed_at;
    if (!confirmedAt) { setTimeLeft('expired'); return; }
    const deadline = new Date(confirmedAt).getTime() + DISPUTE_WINDOW_MS;
    const tick = () => {
      const remaining = deadline - Date.now();
      setTimeLeft(remaining > 0 ? remaining : 'expired');
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [open, mission?.status, mission?.confirmed_at]);

  if (!open) return null;

  const isExpired = timeLeft === 'expired';
  const isCountingDown = typeof timeLeft === 'number';

  const reset = () => {
    setCategory(''); setMessage(''); setPhotos([]); setSubmitting(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 3) {
      toast.error('Maximum 3 preuves photos.');
      return;
    }
    // Compressées avant l'envoi (≈ 1 600 px, JPEG), comme les photos d'annonce (T11)
    files.forEach(f => {
      compressImage(f)
        .then(dataUrl => setPhotos(prev => [...prev, dataUrl]))
        .catch(err => toast.error(`${f.name} : ${err.message}`));
    });
    e.target.value = '';
  };

  const removePhoto = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!category) { toast.error('Sélectionnez une catégorie.'); return; }
    if (message.trim().length < 10) { toast.error('Le message doit faire au moins 10 caractères.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/missions/${missionId}/dispute`, {
        category, message, photos,
      });
      toast.success('Litige ouvert. Notre équipe va arbitrer sous 72h.');
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'ouverture du litige');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="modal-box" style={{ maxWidth: 580, width: '100%' }} data-testid="dispute-modal">
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid rgba(17,24,39,.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B91C1C' }}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--f-display)', fontSize: 17, fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>
                Signaler un problème
              </div>
              <div style={{ fontSize: 11.5, color: '#6B7280', fontFamily: 'var(--f-body)' }}>
                Notre équipe arbitre sous 72h
              </div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={handleClose} aria-label="Fermer" data-testid="btn-dispute-close">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ padding: '18px 20px', maxHeight: '60vh', overflow: 'auto' }}>
          <div className="dispute-info" data-testid="dispute-info-box">
            <strong>Important :</strong> les fonds restent en séquestre tant que le litige n'est pas résolu. Soyez précis et joignez des preuves si possible. Elles accélèrent la résolution.
          </div>

          {isExpired && (
            <div data-testid="dispute-expired-banner" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
              <Clock size={16} style={{ color: '#B91C1C', flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 13, color: '#991B1B', fontFamily: 'var(--f-body)', lineHeight: 1.5 }}>
                <strong>Fenêtre expirée.</strong> La période de contestation de 48h après confirmation de livraison est terminée. Il n'est plus possible d'ouvrir un litige sur cette mission.
              </div>
            </div>
          )}

          {isCountingDown && (
            <div data-testid="dispute-countdown-banner" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <Clock size={15} style={{ color: '#D97706', flexShrink: 0 }} />
              <div style={{ fontSize: 13, color: '#92400E', fontFamily: 'var(--f-body)' }}>
                Fenêtre de litige post-livraison : <strong>{fmtCountdown(timeLeft)}</strong>
              </div>
            </div>
          )}

          <div className="dispute-field">
            <label className="dispute-label">Catégorie du problème <span style={{ color: '#B91C1C' }}>*</span></label>
            <div className="dispute-category-grid" data-testid="dispute-categories">
              {DISPUTE_CATEGORIES.map(c => (
                <button
                  key={c.v}
                  type="button"
                  className={`dispute-category${category === c.v ? ' on' : ''}`}
                  onClick={() => setCategory(c.v)}
                  data-testid={`dispute-cat-${c.v}`}
                >
                  {c.l}
                </button>
              ))}
            </div>
          </div>

          <div className="dispute-field">
            <label className="dispute-label">Décrivez le problème <span style={{ color: '#B91C1C' }}>*</span></label>
            <textarea
              className="dispute-textarea"
              rows={5}
              placeholder="Détaillez ce qui s'est passé : date, lieu, contexte, échanges éventuels avec l'autre partie…"
              value={message}
              onChange={e => setMessage(e.target.value)}
              data-testid="dispute-message"
              maxLength={1000}
            />
            <div className="dispute-counter">{message.length} / 1000</div>
          </div>

          <div className="dispute-field">
            <label className="dispute-label">Preuves photos (optionnel, max 3)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
              data-testid="dispute-photo-input"
            />
            <div className="dispute-photos">
              {photos.map((p, i) => (
                <div key={i} className="dispute-photo">
                  <img src={p} alt={`preuve-${i+1}`} />
                  <button type="button" className="dispute-photo-remove" onClick={() => removePhoto(i)} aria-label="Supprimer" data-testid={`dispute-photo-remove-${i}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <button type="button" className="dispute-photo-add" onClick={() => fileInputRef.current?.click()} data-testid="btn-dispute-add-photo">
                  <Upload size={16} />
                  <span>Ajouter</span>
                </button>
              )}
            </div>
            <div className="dispute-hint">
              <ImageIcon size={11} /> Format JPG/PNG · Max 5 Mo / fichier
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ padding: '14px 20px', borderTop: '1px solid rgba(17,24,39,.07)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="mlc-btn mlc-btn--ghost" onClick={handleClose} data-testid="btn-dispute-cancel">
            Annuler
          </button>
          <button
            type="button"
            className="mlc-btn"
            style={{ background: '#B91C1C', color: '#fff' }}
            disabled={submitting || !category || message.trim().length < 10 || isExpired}
            onClick={handleSubmit}
            data-testid="btn-dispute-submit"
          >
            <AlertTriangle size={14} /> {submitting ? 'Envoi…' : 'Ouvrir le litige'}
          </button>
        </div>
      </div>
    </div>
  );
}
